/**
 * kgob.com/events/{slug} — the public, indexable event page (WHO-OWNS-WHICH-URL.md §3, §4).
 *
 * Static HTML for crawlers and the first paint comes from scripts/events-route.js, which bakes the payload into
 * `window.__EVENTS_EVENT__`. On mount the page re-fetches through our own /api (ANSWERS §5, "re-check on load"):
 * a baked page can be hours old, and between two deploys a date can be added, rescheduled, sold out or cancelled.
 * The volatile lines — the date list, availability badges and whether an RSVP button exists at all — always come
 * from the fresh payload, never from the snapshot.
 *
 * The platform cannot emit a real 301 or 410 outside /api (confirmed with Emergent support, ANSWERS §0), so the
 * four non-live cases are signalled the way Google accepts: a retired slug renders the new event with a canonical
 * to it and replaces the URL; withdrawn, purged and unknown render a noindex notice.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { EventsHead } from "../components/EventsHead";
import { Badge } from "../components/Hero";
import { Notice } from "../components/Notice";
import { GateTransition } from "../components/GateTransition";
import { LocalTime } from "../components/LocalTime";
import { fmtDay, fmtWhen } from "../lib/time";
import { useViewTime } from "../components/AvailabilityRecheck";
import { actionFor, badgeFor, upcomingSessions } from "../lib/availability";
import { fetchEventDetail } from "../lib/detail";
import { buildEventHead } from "../lib/eventHead";
import { gateLink, isSlug, rsvpTarget } from "../lib/gate";
import { placeOf } from "../lib/hero";
import { captureAttribution } from "../lib/attribution";
import { trackPage } from "../lib/analytics";
import { SEO_CONTENT_ID } from "./EventsLanding";
import "../events.css";

/** The baked snapshot, and only for the slug it was baked for. */
const bakedFor = (slug) => {
  const b = (typeof window !== "undefined" && window.__EVENTS_EVENT__) || null;
  return b && b.slug === slug ? b : null;
};

/**
 * The venue, in full. The catalogue sends the street, a map link and the access notes, and our own FAQ promises
 * a visitor will find all three here — they are what decides whether someone drives to Belmont for 8 AM.
 */
function Venue({ session, event }) {
  const v = session.venue;
  if (!v || session.format === "online") return null;
  const street = [v.address_line1, v.address_line2].filter(Boolean).join(", ");
  const town = [v.city, v.region, v.postal_code].filter(Boolean).join(" ");
  return (
    <div className="ev-detail-venue" data-testid={`event-detail-venue-${session.event_session_id}`}>
      {(street || town) && <span className="ev-detail-address">{[street, town].filter(Boolean).join(" · ")}</span>}
      {event.venue_partner && <span className="ev-detail-partner">{event.venue_partner}</span>}
      {v.map_url && (
        <a className="ev-detail-map" href={v.map_url} target="_blank" rel="noreferrer noopener"
           data-testid={`event-detail-map-${session.event_session_id}`}>Open in maps <span aria-hidden="true">↗</span></a>
      )}
      {v.access_notes && <span className="ev-detail-access">{v.access_notes}</span>}
    </div>
  );
}

/** "Registration closes Thu, Sep 24" — a reason to act now, and only while it is still true. */
function closesLine(s, now) {
  if (!s.rsvp_closes_at) return null;
  const closes = new Date(s.rsvp_closes_at);
  if (!(closes > now)) return null;
  return `Registration closes ${fmtDay(s.rsvp_closes_at, s.timezone)}`;
}

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const now = useViewTime();
  const [state, setState] = useState(() => {
    const b = bakedFor(slug);
    return b ? { kind: "live", event: b } : { kind: isSlug(slug) ? "loading" : "missing", event: null };
  });

  useEffect(() => {
    document.getElementById(SEO_CONTENT_ID)?.remove();
    captureAttribution();
    trackPage({ path: `/events/${slug}` });
  }, [slug]);

  // The re-check: the snapshot paints, the fresh payload decides (ANSWERS §5).
  useEffect(() => {
    if (!isSlug(slug)) { setState({ kind: "missing", event: null }); return undefined; }
    let live = true;
    const b = bakedFor(slug);
    setState((s) => (s.event && s.event.slug === slug ? s : b ? { kind: "live", event: b } : { kind: "loading", event: null }));
    fetchEventDetail(slug).then((r) => {
      if (!live) return;
      // A retired slug: the client-side "301", keeping the tracking parameters so attribution survives it.
      if (r.kind === "redirect") { navigate({ pathname: `/events/${r.slug}`, search }, { replace: true }); return; }
      if (r.kind === "event") { setState({ kind: "live", event: r.event }); return; }
      // A blip keeps whatever is already on screen rather than replacing a good page with an error.
      if (r.kind === "unavailable") { setState((s) => (s.event ? s : { kind: "unavailable", event: null })); return; }
      setState({ kind: r.kind === "tombstone" ? "gone" : "missing", event: null });
    });
    return () => { live = false; };
  }, [slug, navigate, search]);

  const { kind, event } = state;
  const head = useMemo(() => buildEventHead({ event, state: kind, now }), [event, kind, now]);
  const sessions = useMemo(() => (event ? upcomingSessions(event, now) : []), [event, now]);
  const notice = {
    gone: ["This event has ended", "It is no longer on the calendar. Here is what is coming up next."],
    unavailable: ["Events are briefly unavailable", "We could not reach the events service just now. Please try again in a moment."],
    loading: ["Loading the event…", "One moment."],
    missing: ["We could not find that event", "The link may be old, or the event may have been renamed."],
  }[kind] || ["We could not find that event", "The link may be old, or the event may have been renamed."];

  return (
    <>
      <EventsHead head={head} />
      <Header />
      <div className="ev-root">
        <main id="ev-main" className="ev-detail" data-testid="event-detail-page">
          {/* A gate bounce can land here carrying ?notice=unavailable */}
          <Notice />
          {event ? (
            <article className="ev-detail-body">
              <nav className="ev-crumbs" aria-label="Breadcrumb">
                <Link to="/events" data-testid="event-detail-crumb-events">Events</Link>
                <span aria-hidden="true"> › </span>
                <span aria-current="page">{event.title}</span>
              </nav>
              {event.images?.hero?.url && (
                <img className="ev-detail-hero" src={event.images.hero.url} alt={event.images.hero.alt || event.title}
                     width={event.images.hero.width || 1200} height={event.images.hero.height || 600} data-testid="event-detail-hero" />
              )}
              <p className="ev-eyebrow">{[event.type?.name || event.type_slug, placeOf(sessions[0])].filter((x) => typeof x === "string" && x).join(" · ") || "Event"}</p>
              <h1 className="ev-detail-title" data-testid="event-detail-title">{event.title}</h1>
              {event.tagline && <p className="ev-detail-tagline">{event.tagline}</p>}
              {event.summary && <p className="ev-detail-summary" data-testid="event-detail-summary">{event.summary}</p>}

              <section className="ev-detail-dates" data-testid="event-detail-dates">
                <h2>Dates</h2>
                {sessions.length === 0 ? (
                  /* An event whose dates have all passed is not an event waiting to be scheduled — saying
                     "dates to be announced" on one reads like a promise nobody made. */
                  (event.event_sessions || []).length ? (
                    <p data-testid="event-detail-past">
                      Every date for this event has passed. <Link to="/events" data-testid="event-detail-past-link">See what is coming up</Link>.
                    </p>
                  ) : (
                    <p data-testid="event-detail-no-dates">Dates to be announced. Check back shortly, or get in touch and we will let you know.</p>
                  )
                ) : (
                  <ul>
                    {(sessions || []).map((s) => {
                      const action = actionFor(s, event, now);
                      return (
                        <li key={s.event_session_id} data-testid={`event-detail-date-${s.event_session_id}`}>
                          <span className="ev-detail-when">{fmtWhen(s)}</span>
                          {placeOf(s) && <span className="ev-detail-where">{placeOf(s)}</span>}
                          <LocalTime local={{ starts_at: s.starts_at, ends_at: s.ends_at, timezone: s.timezone }} />
                          <Badge badge={badgeFor(s, event, now)} />
                          {s.previous_starts_at && (
                            <span className="ev-detail-moved" data-testid={`event-detail-moved-${s.event_session_id}`}>
                              Moved from {fmtDay(s.previous_starts_at, s.timezone)}
                            </span>
                          )}
                          <Venue session={s} event={event} />
                          {action.kind === "rsvp" && (
                            <a className="ev-btn ev-btn-gold" data-rsvp={s.event_session_id}
                               data-testid={`event-detail-rsvp-${s.event_session_id}`}
                               href={gateLink(rsvpTarget(event.slug, s.event_session_id))}>{action.label} <span aria-hidden="true">→</span></a>
                          )}
                          {action.kind === "rsvp" && closesLine(s, now) && (
                            <span className="ev-detail-closes" data-testid={`event-detail-closes-${s.event_session_id}`}>{closesLine(s, now)}</span>
                          )}
                          {action.kind === "rsvp" && event.guests_allowed > 0 && (
                            <span className="ev-detail-guests" data-testid={`event-detail-guests-${s.event_session_id}`}>
                              You may bring up to {event.guests_allowed} named {event.guests_allowed === 1 ? "guest" : "guests"}.
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {event.description_md && (
                <section className="ev-detail-about" data-testid="event-detail-about">
                  <h2>About this event</h2>
                  {String(event.description_md).split(/\n{2,}/).filter(Boolean).map((p, i) => <p key={i}>{p.replace(/^#+\s*/, "")}</p>)}
                </section>
              )}

              {Array.isArray(event.speakers) && event.speakers.length > 0 && (
                <section className="ev-detail-speakers" data-testid="event-detail-speakers">
                  <h2>Who you will hear from</h2>
                  <ul>
                    {(event.speakers || []).map((sp) => (
                      <li key={sp.name}>
                        {sp.photo?.url && <img src={sp.photo.url} alt={sp.photo.alt || sp.name} width="96" height="96" loading="lazy" />}
                        <div>
                          <strong>{sp.name}</strong>
                          {sp.title && <span className="ev-speaker-title">{sp.title}</span>}
                          {sp.bio && <span className="ev-speaker-bio">{sp.bio}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <p className="ev-detail-contact" data-testid="event-detail-contact">
                Questions about this event? Email{" "}
                <a href={`mailto:${event.contact?.email || "events@kgob.com"}`} data-testid="event-detail-email">{event.contact?.email || "events@kgob.com"}</a>
                {event.contact?.phone && <> or call <a href={`tel:${String(event.contact.phone).replace(/\D/g, "")}`} data-testid="event-detail-phone">{event.contact.phone}</a></>}.
              </p>

              <p className="ev-detail-back"><Link to="/events" data-testid="event-detail-back">← All events</Link></p>
            </article>
          ) : (
            <article className="ev-detail-body ev-detail-notice" data-testid={`event-detail-${kind}`}>
              <h1 className="ev-detail-title">{notice[0]}</h1>
              <p>{notice[1]}</p>
              <p><Link className="ev-btn ev-btn-gold" to="/events" data-testid="event-detail-notice-cta">See upcoming events</Link></p>
            </article>
          )}
        </main>
      </div>
      <Footer />
      <GateTransition />
    </>
  );
}
